/**
 * The rail's top strip: Queue N / All / Mine tabs plus the Techs ⌄ dropdown.
 * docs/design-handoff/README.md "Queue rail (pane A)":
 * "Filter row: Queue N / All / Mine tabs (selected = pale blue pill,
 * text-cc-blue-text), then a Techs ⌄ dropdown listing every team member with
 * their comm count and a note ... Choosing one filters the rail to that
 * person's comms and shows an info-subtle banner with a Back button."
 */
import type { QueueFilter } from '../../store'
import { activateOnKey } from './format'

export interface TechOption {
  id: string
  name: string
  count: number
  note: string
}

export interface FilterRowProps {
  filter: QueueFilter
  onChangeFilter: (next: QueueFilter) => void
  queueCount: number
  mineCount: number
  mineUnread: number
  techMenuOpen: boolean
  onToggleTechMenu: () => void
  techOptions: TechOption[]
  activeTechId: string | null
  activeTechLabel: string
  onSelectTech: (id: string) => void
}

function tabClass(selected: boolean): string {
  return [
    'cursor-pointer whitespace-nowrap rounded-lg border px-[11px] py-[7px] text-[11px] font-semibold',
    selected ? 'border-cc-blue bg-cc-blue-pale text-cc-blue-text' : 'border-subtle text-lo',
  ].join(' ')
}

export function FilterRow({
  filter,
  onChangeFilter,
  queueCount,
  mineCount,
  mineUnread,
  techMenuOpen,
  onToggleTechMenu,
  techOptions,
  activeTechId,
  activeTechLabel,
  onSelectTech,
}: FilterRowProps) {
  return (
    <div className="relative flex flex-none items-center gap-[6px] border-b border-subtle px-3 py-[10px]">
      <div data-testid="filter-tab-queue" role="tab" aria-selected={filter === 'queue'} tabIndex={0} className={tabClass(filter === 'queue')} onClick={() => onChangeFilter('queue')} onKeyDown={(e) => activateOnKey(e, () => onChangeFilter('queue'))}>
        Queue {queueCount}
      </div>
      <div data-testid="filter-tab-all" role="tab" aria-selected={filter === 'all'} tabIndex={0} className={tabClass(filter === 'all')} onClick={() => onChangeFilter('all')} onKeyDown={(e) => activateOnKey(e, () => onChangeFilter('all'))}>
        All
      </div>
      <div data-testid="filter-tab-mine" role="tab" aria-selected={filter === 'mine'} tabIndex={0} className={tabClass(filter === 'mine')} onClick={() => onChangeFilter('mine')} onKeyDown={(e) => activateOnKey(e, () => onChangeFilter('mine'))}>
        Mine {mineCount}
        {mineUnread > 0 && (
          <span
            data-testid="filter-tab-mine-badge"
            className="ml-[6px] inline-flex min-w-[16px] items-center justify-center rounded-full bg-brand-primary-strong px-[5px] py-[1px] text-[10px] font-semibold text-on-brand"
          >
            {mineUnread}
          </span>
        )}
      </div>
      <div className="flex-1" />
      <div
        data-testid="techs-menu-button"
        role="button"
        tabIndex={0}
        onClick={onToggleTechMenu}
        onKeyDown={(e) => activateOnKey(e, onToggleTechMenu)}
        className={[
          'cursor-pointer whitespace-nowrap rounded-lg border px-[11px] py-[7px] text-[11px] font-semibold',
          activeTechId ? 'border-cc-blue text-cc-blue-text' : 'border-subtle text-lo',
        ].join(' ')}
      >
        {activeTechLabel} ⌄
      </div>
      {techMenuOpen && (
        <div
          data-testid="techs-menu"
          className="absolute right-3 top-[44px] z-30 w-[196px] overflow-hidden rounded-[10px] border border-subtle bg-elevated shadow-menu"
        >
          <div className="px-[11px] pb-[5px] pt-[9px] text-[10px] font-semibold tracking-[.08em] text-lo">VIEW SOMEONE'S COMMS</div>
          {techOptions.map((t) => (
            <div
              key={t.id}
              data-testid={`tech-option-${t.id}`}
              role="button"
              tabIndex={0}
              onClick={() => onSelectTech(t.id)}
              onKeyDown={(e) => activateOnKey(e, () => onSelectTech(t.id))}
              className={[
                'cursor-pointer border-t border-subtle px-[11px] py-[9px]',
                t.id === activeTechId ? 'bg-cc-blue-pale' : 'bg-transparent',
              ].join(' ')}
            >
              <div className="flex items-baseline justify-between gap-2">
                <div className="text-[12px] font-semibold text-hi">{t.name}</div>
                <div className="whitespace-nowrap font-mono text-[11px] text-lo">
                  {t.count} {t.count === 1 ? 'comm' : 'comms'}
                </div>
              </div>
              <div className="mt-[2px] text-[10px] text-lo">{t.note}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
